import { action, computed, makeObservable, observable } from "mobx";
import { FieldType, ViewModelBase } from "@shoothill/core";
import { APIClient, ICommand, RelayCommand } from "Application";
import { StudentSessionSubTargetModel, StudentSessionSubTargetModelValidator } from "./StudentSessionSubTargetModel";

export class StudentSessionSubTargetViewModel extends ViewModelBase<StudentSessionSubTargetModel> {
    public apiClient = new APIClient();
    //region UI observables

    public _showScoreError: boolean = false;
    public _isDisabled: boolean = true;
    //parent commands
    public onUpdateFieldCommandCallback?: ICommand;

    constructor(model: StudentSessionSubTargetModel = new StudentSessionSubTargetModel(), onUpdateFieldCommandCallback?: ICommand) {
        super(model);
        this.setValidator(new StudentSessionSubTargetModelValidator());
        makeObservable(this, {
            //observables
            _showScoreError: observable,
            _isDisabled: observable,
            //computed
            showScoreError: computed,
            isDisabled: computed,
            //actions
            setShowScoreError: action,
            setIsDisabled: action,
            validateModel: action,
        });
        this.onUpdateFieldCommandCallback = onUpdateFieldCommandCallback;
    }

    //region properties

    public get showScoreError(): boolean {
        return this._showScoreError;
    }

    public get score(): string {
        return this.model.score ? this.model.score.toString() : "";
    }

    public get isDisabled(): boolean {
        return this._isDisabled;
    }

    //region commands

    public onScoreBlurCommand = new RelayCommand(() => {
        this.validateModel();
    });

    public updateScoreCommand = new RelayCommand(
        (value: string) => {
            //remove non-numeric characters
            const regex = /[^0-9]/g;
            let sanitizedValue = value.toString().replace(regex, "");
            sanitizedValue = value.length > 3 ? "10" : value.toString();

            //remove leading zeros
            sanitizedValue = sanitizedValue.replace(/^0+/, "");
            const sanitizedValueNumber = Number.parseInt(sanitizedValue);

            if (sanitizedValueNumber > 10) {
                this.updateField("score", 10);
            } else if (sanitizedValueNumber < 0) {
                this.updateField("score", 0);
            } else {
                this.updateField("score", sanitizedValueNumber);
            }
            const isScoreValid: boolean = this.getError("score") != undefined;
            this.setShowScoreError(isScoreValid);
            this.validateModel();
            this.onUpdateFieldCommandCallback?.execute();
        },
        () => !this.isDisabled,
    );

    //region helpers
    private updateField = (fieldName: keyof FieldType<StudentSessionSubTargetModel>, value: any) => {
        this.setValue(fieldName, value);
        this.isFieldValid(fieldName);
    };

    //region actions

    public setShowScoreError = (value: boolean) => {
        this._showScoreError = value;
    };

    public setIsDisabled = (value: boolean) => {
        this._isDisabled = value;
    };

    public validateModel = () => {
        //validate
        this.isFieldValid("score");
        //get errors
        const isScoreValid: string | undefined = this.getError("score");
        ///set ui errors
        this.setShowScoreError(isScoreValid != undefined);
    };
}
